// src/components/tasks/TaskChecklist.tsx
// Inline checklist component for task details

import { useState, useCallback } from 'react';
import {
  useGetChecklistQuery,
  useCreateChecklistItemMutation,
  useToggleChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useUpdateChecklistItemMutation,
} from '../../api/tasksApi';
import { CheckCircle, Circle, Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import type { TaskChecklistItem } from '../../types/api';

interface TaskChecklistProps {
  taskId: string;
}

export default function TaskChecklist({ taskId }: TaskChecklistProps) {
  const [newItemTitle, setNewItemTitle] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const { data: items = [], isLoading, error } = useGetChecklistQuery(taskId);
  const [createItem, { isLoading: isCreating }] = useCreateChecklistItemMutation();
  const [toggleItem] = useToggleChecklistItemMutation();
  const [deleteItem, { isLoading: isDeleting }] = useDeleteChecklistItemMutation();
  const [updateItem, { isLoading: isUpdating }] = useUpdateChecklistItemMutation();

  // Calculate progress
  const completedCount = items.filter((item) => item.completed).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleAddItem = useCallback(async () => {
    if (!newItemTitle.trim()) return;

    try {
      await createItem({ taskId, title: newItemTitle.trim() }).unwrap();
      setNewItemTitle('');
    } catch (error) {
      console.error('Failed to add checklist item:', error);
    }
  }, [taskId, newItemTitle, createItem]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddItem();
      }
    },
    [handleAddItem]
  );

  const handleToggle = useCallback(
    async (itemId: string) => {
      try {
        await toggleItem({ taskId, itemId }).unwrap();
      } catch (error) {
        console.error('Failed to toggle checklist item:', error);
      }
    },
    [taskId, toggleItem]
  );

  const handleDelete = useCallback(
    async (itemId: string) => {
      try {
        await deleteItem({ taskId, itemId }).unwrap();
      } catch (error) {
        console.error('Failed to delete checklist item:', error);
      }
    },
    [taskId, deleteItem]
  );

  const handleStartEdit = useCallback((item: TaskChecklistItem) => {
    setEditingItemId(item.id);
    setEditingTitle(item.title);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingItemId || !editingTitle.trim()) return;

    try {
      await updateItem({ taskId, itemId: editingItemId, title: editingTitle.trim() }).unwrap();
      setEditingItemId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to update checklist item:', error);
    }
  }, [taskId, editingItemId, editingTitle, updateItem]);

  const handleCancelEdit = useCallback(() => {
    setEditingItemId(null);
    setEditingTitle('');
  }, []);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSaveEdit();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleSaveEdit, handleCancelEdit]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-500 py-2">
        Failed to load checklist
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Checklist
        </label>
        {totalCount > 0 && (
          <span className="text-xs text-gray-500">
            {completedCount} / {totalCount} complete
          </span>
        )}
      </div>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      {/* Checklist items */}
      <div className="space-y-1">
        {items.map((item) => (
          <div
            key={item.id}
            className="group flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-gray-50 transition-colors"
          >
            {/* Drag handle (visual only for now) */}
            <GripVertical className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100 cursor-grab" />

            {/* Checkbox */}
            <button
              onClick={() => handleToggle(item.id)}
              className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
            >
              {item.completed ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <Circle className="h-5 w-5 text-gray-300 hover:text-gray-400" />
              )}
            </button>

            {/* Title (editable) */}
            {editingItemId === item.id ? (
              <input
                type="text"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={handleEditKeyDown}
                onBlur={handleSaveEdit}
                autoFocus
                className="flex-1 px-2 py-0.5 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            ) : (
              <span
                onClick={() => handleStartEdit(item)}
                className={`flex-1 text-sm cursor-pointer ${
                  item.completed ? 'text-gray-400 line-through' : 'text-gray-900'
                }`}
              >
                {item.title}
              </span>
            )}

            {/* Delete button */}
            <button
              onClick={() => handleDelete(item.id)}
              disabled={isDeleting}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new item input */}
      <div className="flex items-center gap-2 pt-1">
        <Plus className="h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={newItemTitle}
          onChange={(e) => setNewItemTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add checklist item..."
          disabled={isCreating}
          className="flex-1 px-2 py-1.5 text-sm border-0 border-b border-gray-200 focus:border-blue-500 focus:ring-0 bg-transparent placeholder-gray-400"
        />
        {isCreating && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>
    </div>
  );
}
